import ClearIcon from '@mui/icons-material/Clear';
import { Theme } from '@mui/material/styles';
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  SelectProps as MUISelectProps,
  Select as MUISelect,
  SelectChangeEvent,
  SxProps,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, ReactNode } from 'react';

import { theme } from '../../styles';

export interface IOptionType {
  key: string | number;
  label: string;
  value?: string;
}

// Omit overriden values, but accept the props the Mui Select accepts.
interface ISelect extends Omit<MUISelectProps, 'onChange' | 'defaultValue'> {
  id: string;
  label?: string;
  value?: string;
  options: IOptionType[];
  disabled?: boolean;
  className?: string;
  onChange: (event: SelectChangeEvent, child: ReactNode) => void;
  showReset?: boolean;
  resetValue?: any; // allow user to define a resetValue
  customClearFunction?: () => void; // allow user to define reset function
  error?: boolean;
  errorMessage?: string;
  touched?: boolean;
  selectAllValue?: string;
  inputLabelStyle?: SxProps<Theme>;
}

export const Select: FC<ISelect> = ({
  customClearFunction,
  selectAllValue,
  onChange,
  label,
  value,
  id,
  options,
  disabled,
  className = '',
  showReset = true,
  resetValue,
  error,
  errorMessage,
  touched,
  inputLabelStyle,
  ...props
}) => {
  const classes = useStyles();

  return (
    <FormControl className={className} error={error && touched} size={props?.size ?? 'small'}>
      {/* DROPDOWN LABEL */}
      {label && (
        <InputLabel id={`${label}-label`} disabled={disabled} sx={inputLabelStyle}>
          {label}
        </InputLabel>
      )}

      <MUISelect
        id={id}
        label={label}
        labelId={`${label}-label`}
        value={value}
        onChange={onChange}
        disabled={disabled}
        {...props}
      >
        {/* SELECT ALL OPTION */}
        {selectAllValue &&
          options?.length > 1 && ( //
            <MenuItem value={selectAllValue}>{selectAllValue}</MenuItem>
          )}

        {/* DROPDOWN OPTIONS */}
        {options?.map(option => (
          <MenuItem key={option.key} value={option.value}>
            {option.label}
          </MenuItem>
        ))}

        {/* EMPTY OPTION PLACEHOLDER */}
        {!options?.length && <MenuItem>No Options to Display.</MenuItem>}
      </MUISelect>

      {/* CLEAR BUTTON */}
      {showReset && !!value && (
        <IconButton
          size={props?.size ?? 'small'}
          className={classes.dropdownClear}
          onClick={() => {
            customClearFunction
              ? customClearFunction()
              : onChange({ target: { value: resetValue } } as SelectChangeEvent, null);
          }}
        >
          <ClearIcon />
        </IconButton>
      )}

      {/* ERROR MESSAGE */}
      {touched && error && (
        <FormHelperText classes={{ root: classes.errorText }}>{errorMessage}</FormHelperText>
      )}
    </FormControl>
  );
};

const useStyles = makeStyles(() => ({
  dropdownClear: {
    fill: theme.palette.grey[400],
    cursor: 'pointer',
    position: 'absolute',
    right: '24px',
    bottom: '3px',
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));
